// The Sound Crystal Quest — story world + chapter/cave structure.
import { LEVELS, getLevel } from "./curriculum";

export const HERO = { name: "You", emoji: "🧒" };
export const COMPANION = { name: "Lumi", emoji: "✨" };

// Age bands. Free Chapter 1 content differs by band, but each band still gets
// only ONE free chapter — picking an older age never unlocks MORE free content.
export const AGE_BANDS = {
  "3-4": { label: "Ages 3–4", sub: "Pre-reader", focus: "Letter sounds", pool: ["feed", "listen", "trace", "echo"], mode: "letters" },
  "5-6": { label: "Ages 5–6", sub: "Early reader", focus: "Sounding out words", pool: ["build", "readmatch", "echoword"], mode: "words" },
  "7-8": { label: "Ages 7–8", sub: "Reader", focus: "Digraphs & sight words", pool: ["digraph", "sight", "echoword"], mode: "advanced" },
};
export const AGE_BAND_KEYS = ["3-4", "5-6", "7-8"];
export function getBand(age_band) {
  return AGE_BANDS[age_band] || AGE_BANDS["3-4"];
}

export const STORY_INTRO = [
  "Long ago, the Great Library of Syllaberry glowed with every word ever read.",
  "But the magic Sound Crystals scattered deep into the Whispering Caves...",
  "and all the words went dark. 🌑",
  "Lumi the wisp needs YOU! Find the crystals, learn their sounds, and light the words again!",
];

// Free Chapter 1 drills ONE fixed practice set (classic first phonics set).
// Every cave + game reuses these same sounds/words — repetition, not new vocab.
// New sounds & words are introduced only in the paid chapters.
export const CH1_LETTERS = ["s", "a", "t", "p", "i", "n", "m"];

// Chapter 1 — The Whispering Caves (FREE). All caves share the SAME practice set.
const CH1_CAVES = [
  { id: 1, title: "Mossy Mouth", sounds: CH1_LETTERS, beat: "Lumi leads you into a cave dripping with moss. \"Listen,\" it whispers." },
  { id: 2, title: "Apple Tunnel", sounds: CH1_LETTERS, beat: "Sweet apple smells fill a twisty tunnel of sound." },
  { id: 3, title: "Pebble Pit", sounds: CH1_LETTERS, beat: "Tiny pebbles ping and echo in the dark." },
  { id: 4, title: "Nook & Den", sounds: CH1_LETTERS, beat: "A cozy den hides a glowing crystal." },
  { id: 5, title: "Foggy Hollow", sounds: CH1_LETTERS, beat: "Thick fog swirls — only sounds can guide you." },
  { id: 6, title: "Rumble Rock", sounds: CH1_LETTERS, beat: "The rocks rumble a deep, rolling sound." },
  { id: 7, title: "Bramble Burrow", sounds: CH1_LETTERS, beat: "Brambles buzz with busy little letters." },
  { id: 8, title: "Crystal Cove", sounds: CH1_LETTERS, beat: "Crystals sparkle on every wall. Almost there!" },
  { id: 9, title: "Echo End", sounds: CH1_LETTERS, beat: "Your voice echoes... echoes... echoes." },
  { id: 10, title: "Dragon's Gate", sounds: CH1_LETTERS, beat: "A huge shadow stirs at the cave's end...", boss: true },
];

export const CH1_CLIFFHANGER = {
  title: "A Wizard Appears!",
  emoji: "🧙",
  lines: [
    "You hold the Crystal of Whispers high — its light floods the whole cave! 💎",
    "Suddenly, a mysterious Wizard steps out of the shadows, his staff glowing bright.",
    "He raises the staff toward the great dragon and begins to chant...",
    "\"Abra... ca... DABRA!\" ⚡",
    "POOF!! 💥 A huge cloud of sparkling smoke swallows the whole gate...",
    "...and when it clears — the dragon is gone. So is the Wizard. What just happened? Nobody knows!",
    "Your adventure continues in Chapter 2 — ask a grown-up to come find out!",
  ],
};

// The single trophy crystal earned for clearing all of Chapter 1.
export const CH1_CRYSTAL = {
  id: "whisper",
  name: "Crystal of Whispers",
  emoji: "💎",
  color: "#38BDF8",
  type: "crystal",
};

// Build the full chapter list. Chapter 1 = caves (free). Chapters 2+ = existing
// mini-game levels, reframed as premium story chapters.
const PREMIUM_LEVEL_ORDER = [2, 3, 7, 4, 5, 6];

export const CHAPTERS = [
  {
    id: 1,
    title: "The Whispering Caves",
    subtitle: "Chapter 1 · Free Adventure",
    type: "caves",
    free: true,
    color: "#8B5CF6",
    icon: "🕳️",
    caves: CH1_CAVES,
    cliffhanger: CH1_CLIFFHANGER,
    crystal: CH1_CRYSTAL,
  },
  ...PREMIUM_LEVEL_ORDER.map((lid, i) => {
    const lvl = getLevel(lid);
    return {
      id: i + 2,
      title: lvl.title,
      subtitle: `Chapter ${i + 2} · ${lvl.phase}`,
      type: "level",
      levelId: lid,
      free: false,
      color: lvl.color,
      icon: lvl.icon,
    };
  }),
];

export function getChapter(id) {
  return CHAPTERS.find((c) => c.id === Number(id));
}

export function getCave(chapterId, caveId) {
  const ch = getChapter(chapterId);
  if (!ch || !ch.caves) return null;
  return ch.caves.find((c) => c.id === Number(caveId));
}

// Treasure rewards offered after each cave.
export const TREASURES = [
  { id: "ruby", emoji: "💎", name: "Ruby Crystal" },
  { id: "star", emoji: "🌟", name: "Gold Star" },
  { id: "crown", emoji: "👑", name: "Tiny Crown" },
  { id: "shell", emoji: "🐚", name: "Magic Shell" },
  { id: "key", emoji: "🗝️", name: "Golden Key" },
  { id: "potion", emoji: "🧪", name: "Glow Potion" },
  { id: "feather", emoji: "🪶", name: "Phoenix Feather" },
  { id: "mushroom", emoji: "🍄", name: "Glow Cap" },
];
